import { lazy, Suspense, useState } from "react"
import FeedbackTab from "./FeedbackTab"

const AnnotationCanvas = lazy(() => import("./AnnotationCanvas"))
const FeedbackForm = lazy(() => import("./FeedbackForm"))

// Convert a data URL to a Blob for FormData upload
function dataUrlToBlob(dataUrl) {
  const [header, data] = dataUrl.split(",")
  const mimeMatch = header.match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg"
  const binary = atob(data)
  const buffer = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i)
  }
  return new Blob([buffer], { type: mime })
}

export default function FeedbackWidget() {
  // steps: idle → flash → capturing → annotating → form → submitting → done
  const [step, setStep] = useState("idle")
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null)
  const [annotatedDataUrl, setAnnotatedDataUrl] = useState(null)
  const [submitError, setSubmitError] = useState(null)

  async function handleTabClick() {
    // Brief white-flash shutter effect, then show the spinner
    setStep("flash")
    await new Promise((r) => setTimeout(r, 250))
    setStep("capturing")
    try {
      const { toJpeg } = await import("html-to-image")
      // Capture the screenshot AND hold the spinner for at least 1.5 s
      const [dataUrl] = await Promise.all([
        toJpeg(document.documentElement, {
          quality: 0.85,
          pixelRatio: Math.min(window.devicePixelRatio, 2),
          width: window.innerWidth,
          height: window.innerHeight,
          backgroundColor: "#ffffff",
          cacheBust: true,
          filter: (el) => !el.classList?.contains("feedback-widget-exclude"),
          // Offset the cloned DOM so the screenshot shows the current viewport,
          // not the top of the page (html-to-image always renders from 0,0)
          style: {
            marginTop: `-${window.scrollY}px`,
            marginLeft: `-${window.scrollX}px`,
            overflow: "hidden",
          },
        }),
        new Promise((r) => setTimeout(r, 1500)),
      ])
      setScreenshotDataUrl(dataUrl)
      setStep("annotating")
    } catch (err) {
      console.error("Screenshot capture failed:", err)
      setStep("form")
    }
  }

  function handleAnnotationConfirm(dataUrl) {
    setAnnotatedDataUrl(dataUrl)
    setStep("form")
  }

  function handleCancel() {
    setStep("idle")
    setScreenshotDataUrl(null)
    setAnnotatedDataUrl(null)
    setSubmitError(null)
  }

  async function handleFormSubmit({ feedbackType, description }) {
    setStep("submitting")
    setSubmitError(null)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const formData = new FormData()
    formData.append("feedback_submission[feedback_type]", feedbackType)
    formData.append("feedback_submission[description]", description)
    formData.append("feedback_submission[page_url]", window.location.href)
    formData.append("feedback_submission[page_title]", document.title)
    formData.append("feedback_submission[user_agent]", navigator.userAgent)
    formData.append("feedback_submission[screen_width]", window.screen.width)
    formData.append("feedback_submission[screen_height]", window.screen.height)
    formData.append("feedback_submission[viewport_width]", window.innerWidth)
    formData.append("feedback_submission[viewport_height]", window.innerHeight)

    const imgDataUrl = annotatedDataUrl || screenshotDataUrl
    if (imgDataUrl) {
      const blob = dataUrlToBlob(imgDataUrl)
      formData.append("feedback_submission[screenshot]", blob, "screenshot.jpg")
    }

    try {
      const response = await fetch("/app/feedback_submissions", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData,
      })

      if (response.ok) {
        setStep("done")
        setTimeout(() => {
          setStep("idle")
          setScreenshotDataUrl(null)
          setAnnotatedDataUrl(null)
        }, 2500)
      } else {
        try {
          const data = await response.json()
          setSubmitError(data.errors?.join(", ") || "Submission failed. Please try again.")
        } catch {
          setSubmitError(`Something went wrong (${response.status}). Please try again.`)
        }
        setStep("form")
      }
    } catch (err) {
      setSubmitError("Network error. Please try again.")
      setStep("form")
    }
  }

  return (
    <>
      {/* Tab button — always visible (hide during active widget steps) */}
      {step === "idle" && (
        <div className="feedback-widget-exclude">
          <FeedbackTab onClick={handleTabClick} />
        </div>
      )}

      {/* Camera shutter flash */}
      {step === "flash" && (
        <div
          className="feedback-widget-exclude fixed inset-0 z-50 bg-white"
          style={{ animation: "kf-flash 250ms ease-out forwards" }}
        />
      )}

      {/* Capturing overlay — visible for at least 1.5 s */}
      {step === "capturing" && (
        <div className="feedback-widget-exclude fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3">
            <div className="relative size-10 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-zinc-100" />
              <div className="absolute inset-0 rounded-full border-2 border-kf-red border-t-transparent animate-spin" />
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-kf-red">
                <path d="M13 5H11.5L10.5 3H5.5L4.5 5H3C2.45 5 2 5.45 2 6V12C2 12.55 2.45 13 3 13H13C13.55 13 14 12.55 14 12V6C14 5.45 13.55 5 13 5ZM8 11C6.62 11 5.5 9.88 5.5 8.5C5.5 7.12 6.62 6 8 6C9.38 6 10.5 7.12 10.5 8.5C10.5 9.88 9.38 11 8 11Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-zinc-800">Capturing screenshot</p>
              <p className="text-xs text-zinc-400 mt-0.5">Getting ready for annotations…</p>
            </div>
          </div>
        </div>
      )}

      {/* Annotation canvas */}
      {step === "annotating" && screenshotDataUrl && (
        <Suspense fallback={null}>
          <div className="feedback-widget-exclude">
            <AnnotationCanvas
              screenshotDataUrl={screenshotDataUrl}
              onConfirm={handleAnnotationConfirm}
              onCancel={handleCancel}
            />
          </div>
        </Suspense>
      )}

      {/* Feedback form */}
      {(step === "form" || step === "submitting") && (
        <Suspense fallback={null}>
          <div className="feedback-widget-exclude">
            <FeedbackForm
              onSubmit={handleFormSubmit}
              onCancel={handleCancel}
              submitting={step === "submitting"}
              error={submitError}
              screenshotDataUrl={annotatedDataUrl || screenshotDataUrl}
            />
          </div>
        </Suspense>
      )}

      {/* Success message */}
      {step === "done" && (
        <div className="feedback-widget-exclude fixed bottom-6 right-6 z-50 bg-zinc-900 text-white text-sm font-medium px-4 py-3 rounded-xl shadow-xl flex items-center gap-2">
          <span className="text-green-400">✓</span>
          Feedback sent — thanks!
        </div>
      )}
    </>
  )
}
